import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useContext } from 'react';
import Datepicker from 'react-tailwindcss-datepicker';
import { AnalyticsRange } from '../contexts/DashboardContext';
import { UserContext } from '../contexts/UserContext';
import { classNames } from '../utils';

interface Props {
  size?: 'x-small' | 'small' | 'normal' | 'large' | 'x-large';
  analyticsRange: AnalyticsRange;
  setAnalyticsRange: (range: AnalyticsRange) => void;
  disabled?: boolean;
}

export default function AnalyticsRangeSelector({
  size = 'x-large',
  analyticsRange,
  setAnalyticsRange,
  disabled,
}: Props) {
  const { selectedAccount } = useContext(UserContext);

  const prefixRanges = [
    'today',
    'yesterday',
    'last7Days',
    'last30Days',
    'currentMonth',
  ];
  const prefixRangesTitles = [
    'Today',
    'Yesterday',
    'Last 7 Days',
    'Last 30 Days',
    'This Month',
  ];

  const customOptions = prefixRanges.map((n, i) => ({
    code: n as AnalyticsRange,
    text: prefixRangesTitles[i],
    period: {
      start: new Date(`192${i}`),
      end: new Date(`192${i}`),
    },
  }));

  return (
    <Datepicker
      key={new Date().getTime()}
      primaryColor="indigo"
      useRange={false}
      value={
        analyticsRange && typeof analyticsRange !== 'string'
          ? {
              startDate: new Date(analyticsRange.startDate || ''),
              endDate: new Date(analyticsRange.endDate || ''),
            }
          : null
      }
      placeholder={
        typeof analyticsRange === 'string'
          ? prefixRangesTitles[prefixRanges.indexOf(analyticsRange)]
          : undefined
      }
      readOnly
      disabled={disabled}
      separator="to"
      minDate={selectedAccount?.data().createdAt?.toDate() || undefined}
      maxDate={new Date(new Date().setDate(new Date().getDate() + 1))}
      onChange={(c) => {
        if (!c) {
          setAnalyticsRange('last30Days');
          return;
        }

        const prefixRange = customOptions.find(
          (p) =>
            p.period.start.getFullYear() ===
              new Date(c.startDate || '').getFullYear() &&
            p.period.end.getFullYear() ===
              new Date(c.endDate || '').getFullYear(),
        );

        if (prefixRange) {
          setAnalyticsRange(prefixRange.code);
          return;
        }

        if (c.startDate && c.endDate) {
          setAnalyticsRange({
            startDate: c.startDate.toISOString(),
            endDate: c.endDate.toISOString(),
          });
          return;
        }

        setAnalyticsRange('last30Days');
      }}
      inputClassName={classNames(
        {
          'x-small': 'px-2',
          'small': 'px-2',
          'normal': 'px-2.5',
          'large': 'px-3',
          'x-large': 'px-3.5',
        }[size],
        {
          'x-small': 'py-1',
          'small': 'py-1',
          'normal': 'py-1.5',
          'large': 'py-2 min-h-[39px]',
          'x-large': 'py-2.5 min-h-[44px]',
        }[size],
        'cursor-pointer relative rounded-md border-0 text-indigo-600 shadow-xs placeholder:text-gray-700 sm:text-sm sm:leading-6',
        'text-gray-700 ring-gray-300 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:ring-gray-200 ring-1 ring-inset font-semibold w-full min-w-[228px]',
      )}
      toggleIcon={() => <CalendarDaysIcon className="h-4 w-4 text-gray-700" />}
      toggleClassName={
        'absolute right-0 h-full px-2 text-indigo-400 focus:outline-hidden disabled:opacity-40 disabled:cursor-not-allowed pointer-events-none'
      }
      showShortcuts
      showFooter
      configs={{
        shortcuts: Object.fromEntries(
          customOptions.map((a) => [`my-${a.code}`, a]),
        ),
      }}
    />
  );
}
